import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { User, Mail, MapPin, Phone, Briefcase, Calendar, ShieldCheck, Loader2, AlertCircle, Camera, Laptop, Cloud, Search, X, CheckCircle2, Users, MessageSquare, ExternalLink, ChevronRight, CheckSquare, FileText, Layout, Send, Printer, Download } from 'lucide-react';
import { SignedImage } from '../components/SignedImage';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getPersonPhotoPath } from '../lib/utils';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

export const Profile: React.FC = () => {
  const { user, authorizeWorkspace } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [persona, setPersona] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Stati per cambio foto
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cacheBust, setCacheBust] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'local' | 'drive'>('local');

  // Stati per Google Drive Integration
  const [googleFiles, setGoogleFiles] = useState<any[]>([]);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleSearchQuery, setGoogleSearchQuery] = useState('');
  const [googleToken, setGoogleToken] = useState<string | null>(() => {
    const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    return isAuthorized ? (sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token')) : null;
  });

  // Stati per Google Workspace in tempo reale
  const [workspaceStats, setWorkspaceStats] = useState<{
    gmail: { total: number; unread: number } | null;
    drive: { fileCount: number; usageBytes: number; limitBytes: number } | null;
    calendar: { upcomingCount: number } | null;
    tasks: { total: number; completed: number } | null;
    contacts: { total: number } | null;
  }>({
    gmail: null,
    drive: null,
    calendar: null,
    tasks: null,
    contacts: null
  });
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false);

  // Stati per invio scheda Profilo (Document Sender Flow)
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [exportFormato, setExportFormato] = useState<'pdf' | 'doc'>('pdf');
  const [exportOrientamento, setExportOrientamento] = useState<'verticale' | 'orizzontale'>('verticale');
  const [exportDestinazione, setExportDestinazione] = useState<'download' | 'stampa' | 'email' | 'drive'>('download');
  const [isExporting, setIsExporting] = useState(false);

  const fetchGoogleWorkspaceAPI = async (endpoint: string) => {
    const token = googleToken || sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
    const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    if (!token || !isAuthorized) return null;
    try {
      const res = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.status === 401 || res.status === 403) {
        return null;
      }
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.warn('Google Workspace API failed in Profile:', e);
      return null;
    }
  };

  const loadProfileWorkspaceStats = async () => {
    const token = googleToken || sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
    const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    if (!token || !isAuthorized) {
      setWorkspaceStats({ gmail: null, drive: null, calendar: null, tasks: null, contacts: null });
      return;
    }

    // Proattivamente controlla e usa la cache della sessione per evitare ricaricamenti molesti
    const cachedStats = sessionStorage.getItem('workspace_hub_stats');
    if (cachedStats) {
      try {
        setWorkspaceStats(JSON.parse(cachedStats));
        return;
      } catch (e) {
        console.warn('Stale workspace cached stats ignored:', e);
      }
    }

    setIsWorkspaceLoading(true);

    const fetchGmail = async () => {
      try {
        const [profile, unreadLabel] = await Promise.all([
          fetchGoogleWorkspaceAPI('https://gmail.googleapis.com/gmail/v1/users/me/profile'),
          fetchGoogleWorkspaceAPI('https://gmail.googleapis.com/gmail/v1/users/me/labels/UNREAD')
        ]);
        return {
          total: profile?.messagesTotal || 0,
          unread: unreadLabel?.messagesUnread || unreadLabel?.messagesTotal || 0
        };
      } catch (e) {
        return { total: 0, unread: 0 };
      }
    };

    const fetchDrive = async () => {
      try {
        const aboutData = await fetchGoogleWorkspaceAPI('https://www.googleapis.com/drive/v3/about?fields=storageQuota');
        let fileCount = 0;
        let pageToken = '';
        let pages = 0;
        try {
          do {
            const url = `https://www.googleapis.com/drive/v3/files?pageSize=100&fields=nextPageToken,files(id)&pageToken=${pageToken}`;
            const driveData = await fetchGoogleWorkspaceAPI(url);
            if (driveData && driveData.files) {
              fileCount += driveData.files.length;
              pageToken = driveData.nextPageToken || '';
            } else {
              break;
            }
            pages++;
          } while (pageToken && pages < 2);
        } catch (fileErr) {}

        return {
          fileCount,
          usageBytes: Number(aboutData?.storageQuota?.usage || 0),
          limitBytes: Number(aboutData?.storageQuota?.limit || 15 * 1024 * 1024 * 1024)
        };
      } catch (e) {
        return { fileCount: 0, usageBytes: 0, limitBytes: 15 * 1024 * 1024 * 1024 };
      }
    };

    const fetchCalendar = async () => {
      try {
        const now = new Date();
        const next7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const calendarData = await fetchGoogleWorkspaceAPI(`https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${now.toISOString()}&timeMax=${next7Days.toISOString()}&singleEvents=true`);
        return {
          upcomingCount: calendarData?.items?.length || 0
        };
      } catch (e) {
        return { upcomingCount: 0 };
      }
    };

    const fetchTasks = async () => {
      try {
        const listsData = await fetchGoogleWorkspaceAPI('https://tasks.googleapis.com/v1/users/@me/lists');
        if (listsData && listsData.items && listsData.items.length > 0) {
          const primaryListId = listsData.items[0].id;
          const tasksData = await fetchGoogleWorkspaceAPI(`https://tasks.googleapis.com/v1/lists/${primaryListId}/tasks?showCompleted=true&showHidden=true&maxResults=50`);
          if (tasksData && tasksData.items) {
            return {
              total: tasksData.items.length,
              completed: tasksData.items.filter((t: any) => t.status === 'completed').length
            };
          }
        }
        return { total: 0, completed: 0 };
      } catch (e) {
        return { total: 0, completed: 0 };
      }
    };

    const fetchContacts = async () => {
      try {
        const contactsData = await fetchGoogleWorkspaceAPI('https://people.googleapis.com/v1/people/me/connections?personFields=names&pageSize=10');
        return {
          total: contactsData?.totalItems || contactsData?.connections?.length || 0
        };
      } catch (e) {
        return { total: 0 };
      }
    };

    try {
      const [gmail, drive, calendar, tasks, contacts] = await Promise.all([
        fetchGmail(),
        fetchDrive(),
        fetchCalendar(),
        fetchTasks(),
        fetchContacts()
      ]);

      const stats = { gmail, drive, calendar, tasks, contacts };
      setWorkspaceStats(stats);
      sessionStorage.setItem('workspace_hub_stats', JSON.stringify(stats));
    } catch (err) {
      console.error('Error fetching Workspace stats in profile:', err);
    } finally {
      setIsWorkspaceLoading(false);
    }
  };

  const handlePreparaEInvia = async () => {
    if (!persona) return;
    setIsExporting(true);
    
    try {
      const fileName = `Scheda_Profilo_${persona.cognome}_${persona.nome}`.replace(/\s+/g, '_');
      const timeStr = new Date().toLocaleDateString('it-IT');
      const isLandscape = exportOrientamento === 'orizzontale';
      
      const docHtml = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <title>Scheda Profilo GEV - ${persona.cognome} ${persona.nome}</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            @page {
              size: ${isLandscape ? 'A4 landscape' : 'A4 portrait'};
              margin: 2cm;
            }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              color: #1e293b;
              line-height: 1.6;
              background-color: #ffffff;
              padding: 10px;
            }
            .header-table {
              width: 100%;
              border-bottom: 3px solid #16a34a;
              margin-bottom: 20px;
              padding-bottom: 10px;
            }
            .header-title {
              font-size: 24px;
              font-weight: bold;
              color: #0f172a;
              margin: 0;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .header-subtitle {
              font-size: 11px;
              color: #4b5563;
              margin: 2px 0 0 0;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              font-weight: 600;
            }
            .header-date {
              text-align: right;
              font-size: 10px;
              color: #9ca3af;
              vertical-align: bottom;
            }
            .section {
              margin-bottom: 25px;
              background: #fafafa;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 15px;
            }
            .section-title {
              font-size: 13px;
              font-weight: 800;
              color: #16a34a;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-top: 0;
              margin-bottom: 12px;
              border-bottom: 1px dashed #e5e7eb;
              padding-bottom: 6px;
            }
            .grid-table {
              width: 100%;
              border-collapse: collapse;
            }
            .grid-table td {
              padding: 8px 4px;
              font-size: 11px;
              vertical-align: top;
            }
            .label {
              font-weight: bold;
              color: #6b7280;
              text-transform: uppercase;
              font-size: 9px;
              width: 30%;
            }
            .value {
              color: #111827;
              font-weight: 500;
            }
            .value-highlight {
              color: #16a34a;
              font-weight: bold;
            }
            .badge-green {
              background-color: #f0fdf4;
              color: #16a53f;
              border: 1px solid #bbf7d0;
              padding: 2px 8px;
              border-radius: 9999px;
              font-weight: bold;
              display: inline-block;
            }
            .footer {
              margin-top: 40px;
              border-top: 1px solid #e5e7eb;
              padding-top: 15px;
              text-align: center;
              font-size: 9px;
              color: #9ca3af;
            }
          </style>
        </head>
        <body>
          <table class="header-table">
            <tr>
              <td>
                <h1 class="header-title">${persona.cognome} ${persona.nome}</h1>
                <p class="header-subtitle">Raggruppamento G.E.V. Bologna | Guardia Ecologica Volontaria</p>
              </td>
              <td class="header-date">
                Generata il: ${timeStr}
              </td>
            </tr>
          </table>

          <div class="section">
            <h2 class="section-title">Anagrafia Principale</h2>
            <table class="grid-table">
              <tr>
                <td class="label">Tipologia Ruolo:</td>
                <td class="value value-highlight">${persona.tipologia_anagrafica || 'Collaboratore'}</td>
                <td class="label">Sesso:</td>
                <td class="value">${persona.sesso || 'N/D'}</td>
              </tr>
              <tr>
                <td class="label">Nascita:</td>
                <td class="value">${persona.data_nascita || 'N/D'}</td>
                <td class="label">Data Iscrizione:</td>
                <td class="value">${persona.data_iscrizione || 'N/D'}</td>
              </tr>
              <tr>
                <td class="label">Luogo Nascita:</td>
                <td class="value" colspan="3">
                  ${[persona.comune_nascita, persona.provincia_nascita, persona.nazione_nascita].filter(Boolean).join(', ') || 'N/D'}
                </td>
              </tr>
            </table>
          </div>

          <div class="section">
            <h2 class="section-title">Dati Professionali & Amministrativi</h2>
            <table class="grid-table">
              <tr>
                <td class="label">Codice Fiscale:</td>
                <td class="value" style="font-family: monospace;">${persona.codice_fiscale || 'N/D'}</td>
                <td class="label">Partita IVA:</td>
                <td class="value" style="font-family: monospace;">${persona.partita_iva || 'N/D'}</td>
              </tr>
              <tr>
                <td class="label">Titolo di Studio:</td>
                <td class="value">${persona.titolo_studio || 'N/D'}</td>
                <td class="label">Tipo Scuola:</td>
                <td class="value">${persona.tipo_scuola || 'N/D'}</td>
              </tr>
              <tr>
                <td class="label">N. Tessera GEV:</td>
                <td class="value">${persona.numero_tessera || 'N/D'}</td>
                <td class="label">Tessera GEV attiva:</td>
                <td class="value">${persona.tessera_attiva ? 'Sì' : 'No'}</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <h2 class="section-title">Contatti & Reperibilità</h2>
            <table class="grid-table">
              <tr>
                <td class="label font-bold">Telefono:</td>
                <td class="value">${persona.telefono || 'N/D'}</td>
                <td class="label font-bold">Email Account:</td>
                <td class="value" style="font-family: monospace;">${user?.email || 'N/D'}</td>
              </tr>
              <tr>
                <td class="label font-bold">Indirizzo Residenza:</td>
                <td class="value" colspan="3">
                  ${[persona.indirizzo, persona.cap, persona.comune_residenza].filter(Boolean).join(', ') || 'N/D'}
                </td>
              </tr>
            </table>
          </div>

          <div class="section">
            <h2 class="section-title">Metadati di Sistema</h2>
            <table class="grid-table">
              <tr>
                <td class="label">Utente Creatore:</td>
                <td class="value">${persona.inserito_da || 'N/D'} (${persona.data_inserimento || 'N/D'})</td>
                <td class="label">Ultima Modifica:</td>
                <td class="value">${persona.modificato_da || 'N/D'} (${persona.data_aggiornamento || 'N/D'})</td>
              </tr>
            </table>
          </div>

          <div class="footer">
            GEV Bologna - Gestione Anagrafica e Volontari Gesvol • Documento Riservato ad Uso Interno dell'Associazione
          </div>
        </body>
        </html>
      `;

      const docBlob = new Blob([docHtml], { type: 'application/msword;charset=utf-8' });

      if (exportDestinazione === 'stampa' || (exportFormato === 'pdf' && exportDestinazione === 'download')) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(docHtml);
          printWindow.document.close();
          printWindow.focus();
          
          setTimeout(() => {
            printWindow.print();
            setIsExporting(false);
            setIsSendModalOpen(false);
            toast.success('Pronto per la stampa! Salva come PDF o stampa direttamente.');
          }, 450);
          return;
        } else {
          toast.error("Finestra di stampa bloccata. Consenti i popup nel browser e riprova.");
          setIsExporting(false);
          return;
        }
      }

      if (exportDestinazione === 'download' && exportFormato === 'doc') {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(docBlob);
        link.download = `${fileName}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setIsExporting(false);
        setIsSendModalOpen(false);
        toast.success(`Documento Word scaricato con successo! (${fileName}.doc)`);
        return;
      }

      if (exportDestinazione === 'email') {
        const mailSubject = encodeURIComponent(`Scheda Profilo GEV - ${persona.cognome} ${persona.nome}`);
        const mailBody = encodeURIComponent(
          `Gentili colleghi,\n\nin allegato sintesi della scheda anagrafica per:\n` +
          `- Cognome e Nome: ${persona.cognome} ${persona.nome}\n` +
          `- Ruolo: ${persona.tipologia_anagrafica || 'GEV'}\n` +
          `- Codice Fiscale: ${persona.codice_fiscale || 'N/D'}\n` +
          `- Codice Tessera: ${persona.numero_tessera || 'N/D'}\n\n` +
          `Il documento completo e modificabile è disponibile sul portale Gesvol.\n` +
          `Cordiali saluti,\n` +
          `${user?.full_name || 'Diego Cimarosa'}`
        );
        
        window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`;
        
        setIsExporting(false);
        setIsSendModalOpen(false);
        toast.info("Apertura del client email in corso...");
        return;
      }

      if (exportDestinazione === 'drive') {
        const token = googleToken || sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
        const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
        
        if (!token || !isAuthorized) {
          toast.error("Account Google Workspace non collegato o disattivato. Collega prima Google Workspace!");
          setIsExporting(false);
          return;
        }

        const ext = exportFormato === 'doc' ? 'doc' : 'html';
        const uploadFileName = `${fileName}.${ext}`;
        const mimeType = exportFormato === 'doc' ? 'application/msword' : 'text/html';

        const metadata = {
          name: uploadFileName,
          mimeType: mimeType,
          description: `Sintesi del profilo GEV compilato da Gesvol per ${persona.cognome} ${persona.nome}`
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', docBlob);

        const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: form
        });

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || "Impossibile salvare il documento su Google Drive.");
        }

        setIsExporting(false);
        setIsSendModalOpen(false);
        toast.success(`Salvato con successo su Google Drive: ${uploadFileName}!`);
        return;
      }

    } catch (err: any) {
      console.error('Errore durante la preparazione o invio del documento:', err);
      toast.error(err.message || 'Errore imprevisto durante la preparazione del documento.');
    } finally {
      setIsExporting(false);
    }
  };

  // Caricamento stats all'avvio o al cambio token
  useEffect(() => {
    loadProfileWorkspaceStats();
  }, [googleToken]);

  // Listener globali per la sincronizzazione dei login in tempo reale
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if ((e.key === 'google_access_token' || e.key === 'google_workspace_authorized') && e.newValue) {
        const isAuth = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
        const updatedToken = localStorage.getItem('google_access_token') || sessionStorage.getItem('google_access_token');
        if (updatedToken && isAuth) {
          setGoogleToken(updatedToken);
        }
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        const token = event.data.session?.provider_token || event.data.session?.provider_access_token;
        if (token) {
          setGoogleToken(token);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    // Se l'utente è loggato e siamo sulla rotta base /profilo, 
    // reindirizziamo alla rotta con ID per coerenza
    if (!id && user?.personaId) {
      navigate(`/user/profilo/${user.personaId}`, { replace: true });
    }
  }, [id, user?.personaId, navigate]);

  useEffect(() => {
    async function fetchPersona() {
      // Priorità: ID dalla URL, altrimenti personaId dell'utente
      const targetId = id || user?.personaId;
      if (!targetId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        // Utilizziamo l'endpoint API server-side che scavalca RLS
        const response = await fetch(`/api/profile/${targetId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setPersona(null);
            return;
          }
          const errorData = await response.json();
          throw new Error(errorData.error || 'Errore durante il caricamento del profilo');
        }

        const data = await response.json();
        setPersona(data);
      } catch (err: any) {
        console.error('Errore recupero persona via API:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchPersona();
  }, [id, user?.id, user?.personaId]);

  // Verifica token Google ad intervalli regolari se la modale è aperta per sincronizzare l'accesso
  useEffect(() => {
    let checkInterval: any;
    if (isPhotoModalOpen && activeTab === 'drive') {
      const checkToken = () => {
        const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
        const token = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
        if (token && isAuthorized) {
          if (googleToken !== token) {
            setGoogleToken(token);
          }
        } else {
          if (googleToken !== null) {
            setGoogleToken(null);
          }
        }
      };

      checkToken();
      checkInterval = setInterval(checkToken, 1500);
    }
    return () => clearInterval(checkInterval);
  }, [isPhotoModalOpen, activeTab, googleToken]);

  // Carica le immagini da drive quando viene attivata la scheda o viene aggiornato il token
  useEffect(() => {
    const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    const token = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
    if (isPhotoModalOpen && activeTab === 'drive' && token && isAuthorized) {
      if (googleToken) {
        fetchGoogleDriveImages(googleSearchQuery);
      }
    }
  }, [isPhotoModalOpen, activeTab, googleToken]);

  const fetchGoogleDriveImages = async (query = '') => {
    const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    const token = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
    if (!token || !isAuthorized) {
      setGoogleToken(null);
      return;
    }
    setIsGoogleLoading(true);
    try {
      let q = "mimeType contains 'image/' and trashed = false";
      if (query) {
        const safeQuery = query.replace(/'/g, "\\'");
        q += ` and name contains '${safeQuery}'`;
      }
      const endpoint = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id,name,mimeType,thumbnailLink)&pageSize=24`;
      const res = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.status === 401) {
        sessionStorage.removeItem('google_access_token');
        localStorage.removeItem('google_access_token');
        sessionStorage.removeItem('google_workspace_authorized');
        localStorage.removeItem('google_workspace_authorized');
        setGoogleToken(null);
        setGoogleFiles([]);
        toast.error('Sessione Google scaduta. Effettua nuovamente l\'autorizzazione.');
        return;
      }

      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        console.warn('Profile: Permission error (403) querying Google Drive.', endpoint, err);
        const detailMsg = err.error?.message || err.message || '';
        setGoogleFiles([]);
        toast.error(`Accesso ai file Google Drive non consentito: ${detailMsg || 'permessi insufficienti.'}`);
        return;
      }
      
      if (!res.ok) {
        throw new Error('Errore durante la richiesta al Drive');
      }
      
      const data = await res.json();
      setGoogleFiles(data.files || []);
    } catch (error: any) {
      console.error('Error fetching Google Drive files:', error);
      toast.error('Errore nel recupero delle immagini da Drive');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (!authorizeWorkspace) {
      toast.error('Google Workspace provider helper non pronto.');
      return;
    }
    try {
      await authorizeWorkspace();
    } catch (err: any) {
      toast.error('Errore autorizzazione: ' + err.message);
    }
  };

  const processAndUploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      
      // Compressione e conversione in WebP
      const compressedFile = await new Promise<File>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const max_size = 600;
            let width = img.width;
            let height = img.height;
            if (width > height) {
              if (width > max_size) {
                height *= max_size / width;
                width = max_size;
              }
            } else {
              if (height > max_size) {
                width *= max_size / height;
                height = max_size;
              }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob) {
                resolve(new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".webp", { type: 'image/webp' }));
              } else {
                reject(new Error("Errore durante la creazione del blob WebP"));
              }
            }, 'image/webp', 0.85);
          };
          img.onerror = () => reject(new Error("Errore nel caricamento dell'immagine"));
          img.src = event.target?.result as string;
        };
        reader.onerror = () => reject(new Error("Errore nella lettura del file"));
        reader.readAsDataURL(file);
      });

      // Convertiamo il file in base64 per l'endpoint proxy
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      const targetPath = getPersonPhotoPath(persona.cognome, persona.nome);

      const response = await fetch('/api/storage/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: targetPath,
          content: base64Content,
          contentType: 'image/webp'
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Errore durante il caricamento');
      }

      toast.success('Foto profilo aggiornata con successo!');
      setCacheBust(Date.now());
      setIsPhotoModalOpen(false);
    } catch (err: any) {
      console.error(err);
      toast.error('Impossibile caricare la foto: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLocalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await processAndUploadFile(e.target.files[0]);
    }
  };

  const handleImportFromGoogleDrive = async (fileId: string, fileName: string) => {
    try {
      setIsUploading(true);
      const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
      const token = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
      if (!token || !isAuthorized) {
        throw new Error('Richiesta autorizzazione Google.');
      }
      
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Impossibile scaricare il file selezionato da Google Drive.');
      }
      
      const blob = await response.blob();
      const virtualFile = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
      await processAndUploadFile(virtualFile);
    } catch (err: any) {
      console.error(err);
      toast.error('Importazione fallita: ' + err.message);
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-muted-foreground">Caricamento profilo...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-destructive">
        <AlertCircle className="w-12 h-12 mb-4" />
        <h2 className="text-xl font-bold">Errore durante il caricamento</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!persona) {
    return (
      <div className="max-w-4xl mx-auto p-4 text-center bg-card rounded-2xl border shadow-sm">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold mb-1">Profilo non trovato</h2>
        <p className="text-sm text-muted-foreground mb-6">
          La tua utenza non è collegata a una voce nell'Anagrafica.
          Contatta l'amministratore per sincronizzare la tua email aziendale: <span className="font-mono text-primary">{user?.email}</span>
        </p>
      </div>
    );
  }

  const isOwnProfile = !id || (persona && user?.personaId === persona.id);
  const isUserAdmin = user?.role === 'Admin' || user?.groups?.some(g => {
    const n = g.name.toLowerCase().replace(/[._\-\s]/g, '');
    return n === 'admins' || n.includes('admin') || n.includes('sistema');
  });
  const canChangePhoto = isOwnProfile || !!isUserAdmin;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight uppercase text-slate-900">
            {isOwnProfile ? 'Il Mio Profilo' : `Profilo`}
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
            {persona.cognome}-{persona.nome}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 bg-slate-100/80 p-1.5 rounded-xl border border-slate-200">
          <button 
            onClick={() => navigate(`/segreteria/anagrafica/persone/${persona.id}`)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-700 hover:text-slate-900 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow-sm border border-slate-200/60 hover:bg-slate-50"
          >
            <User className="w-3.5 h-3.5 text-slate-500" />
            <span>Scheda Persona</span>
          </button>
          {persona.tipologia_anagrafica?.includes('Socio') && (
            <button 
              onClick={() => navigate(`/segreteria/anagrafica/soci/${persona.id}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-700 hover:text-slate-900 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow-sm border border-slate-200/60 hover:bg-slate-50"
            >
              <SignedImage 
                path="images/icons/soci.webp" 
                className="w-4 h-4" 
                fallback={<ShieldCheck className="w-3.5 h-3.5 text-slate-500" />}
              />
              <span>Scheda Socio</span>
            </button>
          )}
          <button 
            type="button"
            onClick={() => setIsSendModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-primary text-primary-foreground hover:bg-primary/95 active:scale-95 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow-sm cursor-pointer"
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Invia scheda Profilo</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pb-12">
        {/* Identità (Top Left) */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center shadow-sm h-full flex flex-col justify-center">
            <div className="relative w-20 h-20 mx-auto mb-3 group">
              <div className="w-full h-full bg-slate-50 rounded-full flex items-center justify-center border-2 border-slate-100 shadow-inner overflow-hidden">
                {(() => {
                  const bucketPhoto = getPersonPhotoPath(persona.cognome, persona.nome);
                  const googlePhoto = isOwnProfile ? user?.avatar_url : null;
                  const altText = `${persona.cognome} ${persona.nome}`;
                  
                  return (
                    <SignedImage 
                      path={bucketPhoto} 
                      alt={altText} 
                      cacheBust={cacheBust}
                      className="w-full h-full object-cover"
                      fallback={
                        googlePhoto ? (
                          <img src={googlePhoto} alt={altText} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <User className="w-8 h-8 text-slate-300" />
                        )
                      }
                    />
                  );
                })()}
              </div>
              {canChangePhoto && (
                <button
                  type="button"
                  onClick={() => setIsPhotoModalOpen(true)}
                  className="absolute bottom-0 right-0 p-1.5 bg-primary hover:opacity-90 text-slate-900 rounded-full border border-white shadow-md hover:scale-110 active:scale-95 transition-all cursor-pointer"
                  title="Cambia Foto"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            
            <div className="min-h-[80px] flex flex-col justify-center">
              <h2 className="text-lg font-black text-slate-900 uppercase leading-tight mb-1">
                {persona.cognome}
                {persona.titolo_onorifico && (
                  <span className="text-[10px] lowercase font-bold text-slate-400 mx-2 italic">
                    {persona.titolo_onorifico}
                  </span>
                )}
                {persona.nome}
              </h2>
              <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">
                {persona.tipologia_anagrafica || 'Collaboratore'}
              </p>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              <div className="flex items-center justify-center py-1 px-3 bg-green-50 rounded-full">
                <ShieldCheck className="w-3 h-3 mr-1.5 text-green-600" />
                <span className="text-[10px] font-bold text-green-700 uppercase tracking-wider">Account Verificato</span>
              </div>
              <div className="text-[10px] font-black text-red-600 uppercase tracking-[0.2em] pt-1">
                Ultimo accesso: {persona.ultimo_accesso || 'MAI'}
              </div>
            </div>
          </div>
        </div>

        {/* Dettagli Anagrafici (Top Right) */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden h-full flex flex-col">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 flex items-center">
                <User className="w-3 h-3 mr-2" />
                Dettagli Anagrafici
              </h3>
            </div>
            
            <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-4 flex-grow">
              {/* Riga 1 */}
              <div className="bg-slate-50/50 p-3 rounded-xl border border-dashed border-slate-200 md:col-span-8 lg:col-span-9">
                <InfoItem icon={<Mail />} label="Email Account" value={user?.email} mono />
              </div>
              <div className="bg-slate-50/50 p-3 rounded-xl border border-dashed border-slate-200 md:col-span-2.5 lg:col-span-2 flex flex-col justify-center min-w-[100px]">
                <div className="flex items-center text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-0.5">
                  <Calendar className="w-3 h-3 mr-1 opacity-60 text-slate-400" />
                  Nascita
                </div>
                <div className="text-sm font-bold text-slate-800">
                  {persona.data_nascita || <span className="text-slate-300 font-normal">N/D</span>}
                </div>
              </div>
              <div className="bg-slate-50/50 p-3 rounded-xl border border-dashed border-slate-200 md:col-span-1.5 lg:col-span-1 flex flex-col justify-center min-w-[65px] text-right md:text-left">
                <div className="flex items-center justify-end md:justify-start text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-0.5">
                  <ShieldCheck className="w-3 h-3 mr-1 opacity-60 text-slate-400" />
                  Sesso
                </div>
                <div className="text-sm font-bold text-slate-800">
                  {persona.sesso || <span className="text-slate-300 font-normal">N/D</span>}
                </div>
              </div>

              {/* Riga 2 */}
              <div className="bg-slate-50/30 p-3 rounded-xl border border-slate-100 md:col-span-4">
                <InfoItem icon={<MapPin />} label="Comune Nascita" value={persona.comune_nascita} />
              </div>
              <div className="bg-slate-50/30 p-3 rounded-xl border border-slate-100 md:col-span-4">
                <InfoItem label="Provincia" value={persona.provincia_nascita} />
              </div>
              <div className="bg-slate-50/30 p-3 rounded-xl border border-slate-100 md:col-span-4">
                <InfoItem label="Paese Nascita" value={persona.nazione_nascita} />
              </div>

              {/* Metadati */}
              <div className="md:col-span-12 grid grid-cols-2 gap-4 mt-auto pt-4 border-t border-slate-100">
                <div className="space-y-0.5">
                   <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Inserito</span>
                   <p className="text-[10px] font-medium text-slate-500 leading-none">{persona.inserito_da}</p>
                   <p className="text-[8px] text-slate-300">{persona.data_inserimento}</p>
                </div>
                <div className="space-y-0.5 text-right">
                   <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Aggiornato</span>
                   <p className="text-[10px] font-medium text-slate-500 leading-none">{persona.modificato_da}</p>
                   <p className="text-[8px] text-slate-300">{persona.data_aggiornamento}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dati Professionali (Bottom Left) */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden h-full flex flex-col">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 flex items-center">
                <Briefcase className="w-3 h-3 mr-2" />
                Dati Prof. & Amm.vi
              </h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4 flex-grow content-start">
              <div className="col-span-1">
                <InfoItem label="Codice Fiscale" value={persona.codice_fiscale} mono />
              </div>
              <div className="col-span-1">
                <InfoItem label="Partita IVA" value={persona.partita_iva?.toString()} mono />
              </div>
              <div className="col-span-1 border-t border-slate-50 pt-3">
                <InfoItem label="Tipo Scuola" value={persona.tipo_scuola} />
              </div>
              <div className="col-span-1 border-t border-slate-50 pt-3">
                <InfoItem label="Titolo Studio" value={persona.titolo_studio} />
              </div>
            </div>
          </div>
        </div>

        {/* Il mio Google Workspace (Bottom Right/Center - lg:col-span-2) */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden h-full flex flex-col">
            <div 
              onClick={() => navigate('/user/workspace')}
              className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center cursor-pointer hover:bg-slate-100/70 transition-all group"
              title="Naviga a Workspace Hub"
            >
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1 group-hover:text-primary transition-colors">
                <Laptop className="w-3.5 h-3.5 mr-1 text-slate-400 group-hover:text-primary" />
                <span>Il mio Google Workspace</span>
              </h3>
              <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-primary">
                <span>Hub</span>
                <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>

            <div className="p-4 flex-grow flex flex-col justify-center min-h-[170px]">
              {(() => {
                const isAuthorized = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
                
                if (!isAuthorized) {
                  return (
                    <div className="flex flex-col items-center justify-center text-center p-4 space-y-3">
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 border border-slate-100">
                        <Cloud className="w-5 h-5 animate-pulse text-blue-500" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 uppercase tracking-tight">Sincronizzazione Richiesta</p>
                        <p className="text-[10px] text-slate-400 max-w-md mx-auto mt-1 leading-relaxed">
                          Collega l'account GEV Bologna per visualizzare l'estratto in tempo reale dei tuoi servizi Workspace direttamente sul profilo.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleGoogleAuth}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-705 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-50 active:scale-95 transition-all shadow-sm cursor-pointer"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                        </svg>
                        Collega Account Google
                      </button>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {/* GMAIL */}
                    <a
                      href="https://mail.google.com"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-2.5 bg-slate-50/50 hover:bg-slate-100 rounded-xl border border-dashed border-slate-200 transition-all cursor-pointer group hover:border-red-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">Gmail</p>
                        <p className="text-xs font-black text-slate-800 truncate">
                          {isWorkspaceLoading ? (
                            <span className="text-[10px] text-slate-400 animate-pulse font-medium">Caricamento...</span>
                          ) : workspaceStats.gmail ? (
                            `${workspaceStats.gmail.unread} non lette`
                          ) : (
                            'Vai alla Posta'
                          )}
                        </p>
                      </div>
                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </a>

                    {/* CALENDAR */}
                    <a
                      href="https://calendar.google.com"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-2.5 bg-slate-50/50 hover:bg-slate-100 rounded-xl border border-dashed border-slate-200 transition-all cursor-pointer group hover:border-blue-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">Calendar</p>
                        <p className="text-xs font-black text-slate-800 truncate">
                          {isWorkspaceLoading ? (
                            <span className="text-[10px] text-slate-400 animate-pulse font-medium">Caricamento...</span>
                          ) : workspaceStats.calendar ? (
                            `${workspaceStats.calendar.upcomingCount} impegni`
                          ) : (
                            'Sfoglia Eventi'
                          )}
                        </p>
                      </div>
                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </a>

                    {/* DRIVE */}
                    <a
                      href="https://drive.google.com"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-2.5 bg-slate-50/50 hover:bg-slate-100 rounded-xl border border-dashed border-slate-200 transition-all cursor-pointer group hover:border-amber-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500 group-hover:scale-110 transition-transform">
                        <Cloud className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">Drive</p>
                        <p className="text-xs font-black text-slate-800 truncate">
                          {isWorkspaceLoading ? (
                            <span className="text-[10px] text-slate-400 animate-pulse font-medium">Caricamento...</span>
                          ) : workspaceStats.drive ? (
                            `${workspaceStats.drive.fileCount} file`
                          ) : (
                            'Apri Cloud'
                          )}
                        </p>
                      </div>
                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </a>

                    {/* TASKS */}
                    <a
                      href="https://mail.google.com/tasks/canvas"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-2.5 bg-slate-50/50 hover:bg-slate-100 rounded-xl border border-dashed border-slate-200 transition-all cursor-pointer group hover:border-indigo-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 group-hover:scale-110 transition-transform">
                        <CheckSquare className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">Tasks</p>
                        <p className="text-xs font-black text-slate-800 truncate">
                          {isWorkspaceLoading ? (
                            <span className="text-[10px] text-slate-400 animate-pulse font-medium">Caricamento...</span>
                          ) : workspaceStats.tasks ? (
                            `${workspaceStats.tasks.completed}/${workspaceStats.tasks.total} attivi`
                          ) : (
                            'Vedi Attività'
                          )}
                        </p>
                      </div>
                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </a>

                    {/* CONTACTS */}
                    <a
                      href="https://contacts.google.com"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-2.5 bg-slate-50/50 hover:bg-slate-100 rounded-xl border border-dashed border-slate-200 transition-all cursor-pointer group hover:border-cyan-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center text-cyan-500 group-hover:scale-110 transition-transform">
                        <Users className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">Contatti</p>
                        <p className="text-xs font-black text-slate-800 truncate">
                          {isWorkspaceLoading ? (
                            <span className="text-[10px] text-slate-400 animate-pulse font-medium">Caricamento...</span>
                          ) : workspaceStats.contacts ? (
                            `${workspaceStats.contacts.total} connessi`
                          ) : (
                            'Apri Rubrica'
                          )}
                        </p>
                      </div>
                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </a>

                    {/* CHAT / MEET */}
                    <a
                      href="https://chat.google.com"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-2.5 bg-slate-50/50 hover:bg-slate-100 rounded-xl border border-dashed border-slate-200 transition-all cursor-pointer group hover:border-emerald-200"
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 group-hover:scale-110 transition-transform">
                        <MessageSquare className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">Chat & Meet</p>
                        <p className="text-xs font-black text-slate-800 truncate">
                          Apri Canali Chat
                        </p>
                      </div>
                      <ExternalLink className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Note - Piena larghezza */}
        <div className="col-span-full">
           <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
             <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
               <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                 Note & Osservazioni
               </h3>
             </div>
             <div className="p-4">
               <p className="text-sm font-medium text-slate-600 italic whitespace-pre-wrap">
                 {persona.note || 'Nessuna nota aggiuntiva.'}
                </p>
              </div>

              {/* MODALE PER IL CAMBIO FOTO PROFILO */}
              {isPhotoModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4">
                  <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-slate-150 dark:border-zinc-800 shadow-2xl max-w-lg w-full flex flex-col overflow-hidden max-h-[85vh]">
                    
                    {/* Header Modale */}
                    <div className="p-4 border-b border-slate-100 dark:border-zinc-800 flex items-center justify-between bg-slate-50 dark:bg-zinc-900/50">
                      <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white uppercase tracking-tight">
                          Modifica Foto Profilo
                        </h3>
                        <p className="text-xs text-slate-500">
                          {persona.cognome} {persona.nome}
                        </p>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => setIsPhotoModalOpen(false)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-zinc-800 rounded-lg transition-all text-slate-400 hover:text-slate-600 dark:hover:text-white cursor-pointer"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Navigazione Tab */}
                    <div className="flex border-b border-slate-100 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                      <button
                        type="button"
                        onClick={() => setActiveTab('local')}
                        className={`flex-1 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center justify-center gap-2 ${
                          activeTab === 'local' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-zinc-350'
                        }`}
                      >
                        <Laptop className="w-4 h-4" />
                        PC Locale
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('drive');
                          if (!googleToken) {
                            const token = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
                            if (token) setGoogleToken(token);
                          }
                        }}
                        className={`flex-1 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center justify-center gap-2 ${
                          activeTab === 'drive' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-zinc-350'
                        }`}
                      >
                        <Cloud className="w-4 h-4" />
                        Google Drive GEV
                      </button>
                    </div>

                    {/* Contenuto Tab */}
                    <div className="p-6 overflow-y-auto flex-grow bg-slate-50/30 dark:bg-zinc-900/20">
                      {isUploading ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-4">
                          <Loader2 className="w-10 h-10 text-primary animate-spin" />
                          <p className="text-sm font-bold text-slate-600 dark:text-zinc-300 uppercase tracking-wider animate-pulse font-sans">
                            Ottimizzazione ed upload in corso...
                          </p>
                        </div>
                      ) : activeTab === 'local' ? (
                        <div className="space-y-4">
                          <div className="border-2 border-dashed border-slate-200 dark:border-zinc-800 rounded-2xl p-8 text-center bg-white dark:bg-zinc-900 hover:border-primary/50 transition-all flex flex-col items-center justify-center group relative cursor-pointer font-sans">
                            <input 
                              type="file" 
                              accept="image/*"
                              onChange={handleLocalFileChange}
                              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
                            />
                            <div className="w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-primary mb-3 group-hover:scale-110 transition-transform">
                              <Camera className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-bold text-slate-700 dark:text-zinc-200 mb-1">
                              Trascina o seleziona la foto dal computer
                            </p>
                            <p className="text-[11px] text-slate-400">
                              Verrà automaticamente convertita e ottimizzata in formato leggero WebP
                            </p>
                          </div>
                        </div>
                      ) : (
                        /* Google Drive */
                        <div className="space-y-4 font-sans">
                          {!googleToken ? (
                            <div className="text-center py-8 px-4 bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 rounded-2xl shadow-sm">
                              <div className="w-12 h-12 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mx-auto mb-3 text-primary">
                                <Cloud className="w-6 h-6" />
                              </div>
                              <h4 className="text-sm font-black text-slate-800 dark:text-zinc-100 uppercase tracking-tight mb-2">
                                Autorizzazione Google Richiesta
                              </h4>
                              <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">
                                Per sfogliare in sicurezza le foto dell'account GEV Bologna (<span className="font-semibold">@gevbologna.org</span>), collega l'applicazione a Google Drive.
                              </p>
                              
                              <button
                                type="button"
                                onClick={handleGoogleAuth}
                                className="inline-flex items-center gap-2 px-6 py-2.5 bg-white border border-slate-250 text-slate-700 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 active:scale-98 transition-all shadow-sm cursor-pointer"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24">
                                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                </svg>
                                Collega Google Drive
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-3 flex flex-col h-full">
                              {/* Search Bar */}
                              <div className="relative">
                                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                <input
                                  type="text"
                                  placeholder="Cerca immagini (.jpg, .png, .webp)..."
                                  value={googleSearchQuery}
                                  onChange={(e) => setGoogleSearchQuery(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      fetchGoogleDriveImages(googleSearchQuery);
                                    }
                                  }}
                                  className="w-full pl-9 pr-24 py-2 border border-slate-200 dark:border-zinc-800 rounded-xl bg-white dark:bg-zinc-900 text-xs text-slate-800 dark:text-zinc-250 focus:outline-none focus:border-primary font-bold shadow-inner font-sans"
                                />
                                <button
                                  type="button"
                                  onClick={() => fetchGoogleDriveImages(googleSearchQuery)}
                                  className="absolute right-1.5 top-1.5 px-3 py-1 bg-primary text-slate-900 rounded-lg text-[10px] font-black uppercase tracking-wider hover:opacity-90 active:scale-95 transition-all cursor-pointer"
                                >
                                  Cerca
                                </button>
                              </div>

                              {/* Files list or Loading */}
                              {isGoogleLoading ? (
                                <div className="flex flex-col items-center justify-center py-16">
                                  <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
                                  <p className="text-xs text-slate-400 animate-pulse font-semibold uppercase tracking-wider">Navigazione Google Drive...</p>
                                </div>
                              ) : googleFiles.length === 0 ? (
                                <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-2xl border border-slate-100 dark:border-zinc-800">
                                  <Cloud className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                  <p className="text-xs text-slate-400 font-semibold uppercase">Nessuna immagine trovata.</p>
                                  <p className="text-[10px] text-slate-400 font-normal">Prova a cercare con un altro termine o verifica le tue cartelle GEV.</p>
                                </div>
                              ) : (
                                <div className="grid grid-cols-3 gap-2 overflow-y-auto max-h-[280px] p-0.5">
                                  {googleFiles.map((file) => (
                                    <div
                                      key={file.id}
                                      onClick={() => handleImportFromGoogleDrive(file.id, file.name)}
                                      className="border border-slate-150 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-xl p-1.5 cursor-pointer hover:border-primary hover:shadow-md transition-all flex flex-col items-center text-center group relative h-[105px] justify-between overflow-hidden resize-none"
                                    >
                                      <div className="w-full h-14 rounded-lg bg-slate-50 dark:bg-zinc-950 flex items-center justify-center overflow-hidden border border-slate-100 dark:border-zinc-800/50">
                                        {file.thumbnailLink ? (
                                          <img 
                                            src={file.thumbnailLink} 
                                            alt={file.name} 
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform" 
                                            referrerPolicy="no-referrer"
                                          />
                                        ) : (
                                          <Camera className="w-5 h-5 text-slate-300" />
                                        )}
                                      </div>
                                      <span className="text-[9px] font-bold text-slate-600 dark:text-zinc-400 truncate w-full px-1">
                                        {file.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer Modale */}
                    <div className="p-3 border-t border-slate-100 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setIsPhotoModalOpen(false)}
                        className="px-4 py-2 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-50 dark:hover:bg-zinc-750 transition-all cursor-pointer"
                      >
                        Annulla
                      </button>
                    </div>

                  </div>
                </div>
              )}

              {/* MODALE PER L'INVIO E ESPORTAZIONE DELLA SCHEDA PROFILO */}
              {isSendModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4">
                  <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-slate-150 dark:border-zinc-800 shadow-2xl max-w-lg w-full flex flex-col overflow-hidden max-h-[85vh]">
                    
                    {/* Header Modale */}
                    <div className="p-4 border-b border-slate-100 dark:border-zinc-800 flex items-center justify-between bg-slate-50 dark:bg-zinc-900/50">
                      <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white uppercase tracking-tight">
                          Invia Scheda Profilo
                        </h3>
                        <p className="text-xs text-slate-500">
                          Prepara e esporta la scheda anagrafica di {persona.cognome} {persona.nome}
                        </p>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => setIsSendModalOpen(false)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-zinc-800 rounded-lg transition-all text-slate-400 hover:text-slate-600 dark:hover:text-white cursor-pointer"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Contenuto Options */}
                    <div className="p-5 overflow-y-auto space-y-4">
                      
                      {/* Opzione 1: Formato */}
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-350 block mb-2">
                          1. Formato Documento
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setExportFormato('pdf')}
                            className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all cursor-pointer ${
                              exportFormato === 'pdf'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'
                            }`}
                          >
                            <FileText className="w-6 h-6 mb-1 text-red-500" />
                            <span className="text-xs font-bold uppercase tracking-wide">Documento PDF</span>
                            <span className="text-[9px] opacity-75">Layout Pronto Stampa</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setExportFormato('doc')}
                            className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all cursor-pointer ${
                              exportFormato === 'doc'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'
                            }`}
                          >
                            <Briefcase className="w-6 h-6 mb-1 text-blue-500" />
                            <span className="text-xs font-bold uppercase tracking-wide">Word (DOC)</span>
                            <span className="text-[9px] opacity-75">Modificabile ed Editabile</span>
                          </button>
                        </div>
                      </div>

                      {/* Opzione 2: Orientamento */}
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-350 block mb-2">
                          2. Orientamento di Pagina
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setExportOrientamento('verticale')}
                            className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer text-xs font-bold uppercase ${
                              exportOrientamento === 'verticale'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'
                            }`}
                          >
                            <Layout className="w-4 h-4 text-slate-500" />
                            Verticale (Portrait)
                          </button>

                          <button
                            type="button"
                            onClick={() => setExportOrientamento('orizzontale')}
                            className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer text-xs font-bold uppercase ${
                              exportOrientamento === 'orizzontale'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'
                            }`}
                          >
                            <Layout className="w-4 h-4 text-slate-500 rotate-90" />
                            Orizzontale (Landscape)
                          </button>
                        </div>
                      </div>

                      {/* Opzione 3: Destinazione */}
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-350 block mb-2">
                          3. Scegli Destinazione / Azione
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          
                          {/* DOWNLOAD */}
                          <div
                            onClick={() => setExportDestinazione('download')}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                              exportDestinazione === 'download'
                                ? 'border-primary bg-primary/5'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${exportDestinazione === 'download' ? 'bg-primary/10 text-primary' : 'bg-slate-50 text-slate-500'}`}>
                              <Download className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold leading-none mb-1 text-slate-800">Scarica File</p>
                              <p className="text-[9px] text-slate-400 leading-tight">Salva direttamente sul dispositivo</p>
                            </div>
                          </div>

                          {/* STAMPA / PDF DIALOG */}
                          <div
                            onClick={() => setExportDestinazione('stampa')}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                              exportDestinazione === 'stampa'
                                ? 'border-primary bg-primary/5'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${exportDestinazione === 'stampa' ? 'bg-primary/10 text-primary' : 'bg-slate-50 text-slate-500'}`}>
                              <Printer className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold leading-none mb-1 text-slate-800">Stampa o PDF</p>
                              <p className="text-[9px] text-slate-400 leading-tight">Apre dialogo di stampa di sistema</p>
                            </div>
                          </div>

                          {/* EMAIL CANALE */}
                          <div
                            onClick={() => setExportDestinazione('email')}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                              exportDestinazione === 'email'
                                ? 'border-primary bg-primary/5'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${exportDestinazione === 'email' ? 'bg-primary/10 text-primary' : 'bg-slate-50 text-slate-500'}`}>
                              <Send className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold leading-none mb-1 text-slate-800">Prepara Email</p>
                              <p className="text-[9px] text-slate-400 leading-tight">Invia tramite il programma email</p>
                            </div>
                          </div>

                          {/* GOOGLE DRIVE */}
                          <div
                            onClick={() => setExportDestinazione('drive')}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                              exportDestinazione === 'drive'
                                ? 'border-primary bg-primary/5'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${exportDestinazione === 'drive' ? 'bg-primary/10 text-primary' : 'bg-slate-50 text-slate-500'}`}>
                              <Cloud className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold leading-none mb-1 text-slate-800">Google Drive</p>
                              <p className="text-[9px] text-slate-400 leading-tight">Salva direttamente in Cloud GEV</p>
                            </div>
                          </div>

                        </div>
                      </div>

                    </div>

                    {/* Footer Modale con Azione */}
                    <div className="p-3 border-t border-slate-100 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setIsSendModalOpen(false)}
                        className="px-4 py-2 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-50 dark:hover:bg-zinc-750 transition-all cursor-pointer"
                      >
                        Annulla
                      </button>
                      <button
                        type="button"
                        disabled={isExporting}
                        onClick={handlePreparaEInvia}
                        className="px-5 py-2 bg-primary text-primary-foreground rounded-xl text-[10px] font-black uppercase tracking-wider hover:opacity-95 active:scale-95 transition-all shadow-md shadow-primary/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                      >
                        {isExporting ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Elaborazione...
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5" />
                            Elabora e Invia
                          </>
                        )}
                      </button>
                    </div>

                  </div>
                </div>
              )}
              
              <div className="hidden">
                <p>
                </p>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

const InfoItem: React.FC<{ icon?: React.ReactNode; label: string; value?: string | null; mono?: boolean }> = ({ icon, label, value, mono }) => (
  <div className="space-y-0.5">
    <div className="flex items-center text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">
      {icon && <span className="mr-1.5 opacity-60">{React.cloneElement(icon as React.ReactElement, { className: 'w-3.5 h-3.5' })}</span>}
      {label}
    </div>
    <div className={`text-sm font-bold text-slate-800 ${mono ? 'font-mono' : ''}`}>
      {value || <span className="text-slate-300 font-normal">N/D</span>}
    </div>
  </div>
);
